import classNames from "classnames";
import { DateTime } from "luxon";
import QRCode from "qrcode.react";
import { useState } from "react";

import { LISCase, LISLabOrder, PatientInsurance } from "pwapi";

import { selectFeatureFlag } from "shared/reducers/sysReducer";

import { useAppSelector } from "app/hooks";
import { ReactComponent as PWLogoSVG } from "assets/pw-sonic-logo.svg";
import { useSelectedLab } from "hooks/useSelectedLabStation";
import UserFullName from "ui/components/UserFullName";
import { dashZipcode } from "util/addressUtils";
import { labelForBlock } from "util/blockUtils";
import { specimenSort } from "util/sortFunctions";

const OrderRow = ({
  lisCase,
  order,
  hiddenOrderIds,
  setHiddenOrderIds,
}: {
  lisCase: LISCase;
  order: LISLabOrder;
  hiddenOrderIds: number[];
  setHiddenOrderIds: React.Dispatch<React.SetStateAction<number[]>>;
}) => {
  const canPrintLabOrdersBlockBarcodeFlag = useAppSelector((state) =>
    selectFeatureFlag(state, "canPrintLabOrdersBlockBarcode")
  );
  const block = lisCase.blocks.find((b) => b.id === order?.block_id);
  const specimen = lisCase.specimens?.find((s) => s.id === block?.specimen_id);
  const orderLabStain = useAppSelector(({ labStains }) =>
    labStains[order?.lab_stain_id || -1]
      ? labStains[order?.lab_stain_id || -1]
      : undefined
  );
  // TODO: it _is_ possible for the stains not to be found when frontloaded,
  // ... so the type signature here should be | undefined
  // This is a mouthful every time you need to select for a stain, so let's
  // ... refactor this into a re-usable selector instead
  const orderStain = useAppSelector(({ stains }) =>
    stains[orderLabStain?.stain_id || -1]
      ? stains[orderLabStain?.stain_id || -1]
      : undefined
  );

  const [tissueType, setTissueType] = useState<"USS" | "BLK">();
  const [ctrlLabel, setCtrlLabel] = useState<boolean>();
  const [slideLabel, setSlideLabel] = useState<boolean>();

  // Just to make TypeScript shut up; order.id will not be undefined
  if (!order.id) return null;

  return (
    <tr
      key={order.id}
      className={classNames("SendoutRequisitionOrderRow", {
        "SendoutRequisitionOrderRow--hidden": hiddenOrderIds.includes(order.id),
      })}
    >
      <td>
        <input
          type="checkbox"
          checked={!hiddenOrderIds.includes(order.id)}
          onChange={(e) => {
            setHiddenOrderIds((hiddenOrderIds) => {
              return e.target.checked
                ? hiddenOrderIds.filter((h) => h !== order.id)
                : order.id
                ? [...hiddenOrderIds, order.id]
                : hiddenOrderIds;
            });
          }}
        />
      </td>
      <td className="SendoutRequisitionOrderRow__accession">
        {lisCase.accession}
      </td>
      <td>
        {labelForBlock(block, lisCase)}
        {canPrintLabOrdersBlockBarcodeFlag && (
          <figure title={`block-${block?.id}`} role="figure">
            <QRCode
              value={`block-${block?.id}`}
              size={32}
              bgColor={"#ffffff"}
              fgColor={"var(--black)"}
              level={"M"}
              includeMargin={false}
              renderAs={"svg"}
            />
          </figure>
        )}
      </td>
      <td>
        {orderStain?.category}:<br />
        {orderStain?.name} <code>({orderStain?.abbreviation})</code>
      </td>
      <td>
        <div
          className={classNames("SendoutRequisition__Input", {
            "SendoutRequisition__Input--value": tissueType,
          })}
        >
          <label>
            <input
              type="radio"
              name={`tissue_type_${order?.id}`}
              value="USS"
              checked={tissueType === "USS"}
              onChange={() => setTissueType("USS")}
            />
            &nbsp;USS
          </label>
          <br />
          <label>
            <input
              type="radio"
              name={`tissue_type_${order?.id}`}
              value="BLK"
              checked={tissueType === "BLK"}
              onChange={() => setTissueType("BLK")}
            />
            &nbsp;BLK
          </label>
        </div>
        {tissueType && (
          <div className="SendoutRequisition__Value">{tissueType}</div>
        )}
      </td>
      <td>{specimen?.site?.toUpperCase()}</td>
      <td>
        {specimen?.collected_date &&
          DateTime.fromISO(specimen.collected_date).toFormat("MM/dd/yyyy")}
      </td>
      <td>
        <div
          className={classNames("SendoutRequisition__Input", {
            "SendoutRequisition__Input--value":
              typeof ctrlLabel !== "undefined",
          })}
        >
          {" "}
          <label>
            <input
              type="radio"
              name={`ctrl_label_${order?.id}`}
              checked={ctrlLabel === true}
              onChange={() => setCtrlLabel(true)}
            />
            &nbsp;Y
          </label>
          <br />
          <label>
            <input
              type="radio"
              name={`ctrl_label_${order?.id}`}
              checked={ctrlLabel === false}
              onChange={() => setCtrlLabel(false)}
            />
            &nbsp;N
          </label>
        </div>
        {typeof ctrlLabel !== "undefined" && (
          <div className="SendoutRequisition__Value">
            {ctrlLabel === true && "Yes"}
            {typeof ctrlLabel !== "undefined" && ctrlLabel === false && "No"}
          </div>
        )}
      </td>

      <td>
        <div
          className={classNames("SendoutRequisition__Input", {
            "SendoutRequisition__Input--value":
              typeof slideLabel !== "undefined",
          })}
        >
          {" "}
          <label>
            <input
              type="radio"
              name={`slide_label_${order?.id}`}
              checked={slideLabel === true}
              onChange={() => setSlideLabel(true)}
            />
            &nbsp;Y
          </label>
          <br />
          <label>
            <input
              type="radio"
              name={`slide_label_${order?.id}`}
              checked={slideLabel === false}
              onChange={() => setSlideLabel(false)}
            />
            &nbsp;N
          </label>
        </div>
        {typeof slideLabel !== "undefined" && (
          <div className="SendoutRequisition__Value">
            {slideLabel === true && "Yes"}
            {typeof slideLabel !== "undefined" && slideLabel === false && "No"}
          </div>
        )}
      </td>
    </tr>
  );
};

const SendoutRequisition = ({
  lisCase,
  orders,
  externalLabId,
  hidden,
}: {
  lisCase: LISCase;
  orders: LISLabOrder[];
  externalLabId?: number;
  hidden?: boolean;
}) => {
  const { patient, accessioning_lab_id } = lisCase;
  const accessioningLab = useAppSelector(({ labs }) =>
    accessioning_lab_id ? labs[accessioning_lab_id] : undefined
  );
  const externalLab = useAppSelector(({ externalLabs }) =>
    externalLabs[externalLabId || -1]
      ? externalLabs[externalLabId || -1]
      : undefined
  );
  const selectedLab = useSelectedLab();
  const sendingLab = accessioningLab || selectedLab;
  const sendoutLab = externalLab || { name: "" };

  const sendingUser = useAppSelector(
    (state) => state.users[lisCase.active_assignment_id || 0]
  );

  // Utilizing a single state variable to determine who is going to be billed.
  // TODO: 'self' here is an attempt to make removal of a the billing_type value easier
  const [billTo, setBillTo] = useState<
    LISCase.billing_type | "self" | undefined
  >(
    lisCase.self_pay ||
      (lisCase.billing_type as LISCase.billing_type | "cash") === "cash"
      ? "self"
      : undefined
  );
  const [hiddenOrderIds, setHiddenOrderIds] = useState<number[]>([]);

  const insurance =
    lisCase.patient.insurance
      ?.filter((i: PatientInsurance) => i.primary_provider === true)
      .at(0) ||
    lisCase.patient.insurance?.at(0) ||
    undefined;

  const sendoutOrders = orders.filter(
    (o) => o.id && !hiddenOrderIds.includes(o.id)
  );
  const specimens = lisCase.specimens
    .filter((s) =>
      lisCase.blocks
        .filter((b) =>
          sendoutOrders.map((order) => order?.block_id).includes(b.id)
        )
        .map((b) => b.specimen_id)
        .includes(s.id)
    )
    .sort(specimenSort);

  const [notes, setNotes] = useState("");

  return (
    <article
      className={classNames("SendoutRequisition", {
        "SendoutRequisition--hidden": hidden || sendoutOrders?.length === 0,
      })}
    >
      <header className="SendoutRequisition__header">
        <div>
          <figure>
            <PWLogoSVG />
          </figure>
        </div>

        <div>
          <h1>Sendout Requisition</h1>
          <p>
            <br />
            Phone: (877) 463.5434 • Fax: (366) 360-0086
            <br />
            PathologyWatch.com
          </p>
        </div>
      </header>

      <section>
        <table>
          <thead>
            <tr>
              <th>Accession</th>
              <th>Sendout Lab</th>
              <th>Priority</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{lisCase?.accession}</td>
              <td className="SendoutRequisition__Fillable">
                <input type="text" defaultValue={sendoutLab?.name} />
              </td>
              <td>
                {lisCase?.priority === LISCase.priority.NORMAL ? (
                  "ROUTINE"
                ) : (
                  <strong>{lisCase.priority?.toUpperCase()}</strong>
                )}
              </td>
              <td>{DateTime.now().toFormat("MM/dd/yyyy")}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <table className="SendoutRequisitionPrintWrapper">
        <thead>
          <tr>
            <td>
              <section>
                <table>
                  <thead>
                    <tr>
                      <th colSpan={2}>Patient Information</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <dl>
                          <dt>Last Name:</dt>
                          <dd>{patient.last_name}</dd>

                          <dt>First Name:</dt>
                          <dd>{patient.first_name}</dd>

                          <dt>Date of Birth:</dt>
                          <dd>
                            {patient.date_of_birth && // 1900-01-01
                              DateTime.fromFormat(
                                patient.date_of_birth,
                                "yyyy-MM-dd"
                              ).toFormat("MM/dd/yyyy")}
                          </dd>

                          <dt>Gender:</dt>
                          <dd>{patient.sex}</dd>
                        </dl>
                      </td>
                      <td>
                        <dl>
                          <dt>Address:</dt>
                          <dd>
                            {[patient.address?.street, patient.address?.street2]
                              .filter((v) => v)
                              .join(", ")}
                            <br />
                            {[patient.address?.city, patient.address?.state]
                              .filter((v) => v)
                              .join(", ")}
                            {patient.address?.zipcode && (
                              <> {dashZipcode(patient.address?.zipcode)}</>
                            )}
                          </dd>

                          <dt>Phone #:</dt>
                          <dd>
                            {patient.address?.phone1 || patient.address?.phone2}
                          </dd>

                          <dt>MRN:</dt>
                          <dd>{patient.medical_record_number}</dd>
                        </dl>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </section>
            </td>
          </tr>
        </thead>

        <tbody>
          <div className="SendoutRequisitionBody">
            <section>
              <table>
                <thead>
                  <tr>
                    <th>Billing Information</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <dl className="SendoutRequisitionBillTo">
                        <dt>Bill To:</dt>
                        <dd>
                          <div
                            className={classNames("SendoutRequisition__Input", {
                              "SendoutRequisition__Input--value": billTo,
                            })}
                          >
                            <label>
                              <input
                                type="radio"
                                name={`bill_to_${sendoutLab?.name}`}
                                value={LISCase.billing_type.GLOBAL}
                                checked={billTo === LISCase.billing_type.GLOBAL}
                                onChange={() =>
                                  setBillTo(LISCase.billing_type.GLOBAL)
                                }
                                disabled={!insurance}
                              />{" "}
                              Insurance
                            </label>
                            <label>
                              <input
                                type="radio"
                                name={`bill_to_${sendoutLab?.name}`}
                                value={"self"}
                                checked={billTo === "self"}
                                onChange={() => setBillTo("self")}
                              />{" "}
                              Self Pay
                            </label>
                            <label>
                              <input
                                type="radio"
                                name={`bill_to_${sendoutLab?.name}`}
                                value={LISCase.billing_type.CLIENT}
                                checked={billTo === LISCase.billing_type.CLIENT}
                                onChange={() =>
                                  setBillTo(LISCase.billing_type.CLIENT)
                                }
                              />{" "}
                              Client Bill
                            </label>
                          </div>
                          {billTo && (
                            <div className="SendoutRequisition__Value">
                              {billTo === LISCase.billing_type.GLOBAL &&
                                "Insurance"}
                              {billTo === "self" && "Self Pay"}
                              {billTo === LISCase.billing_type.CLIENT &&
                                "Client Bill"}
                            </div>
                          )}
                        </dd>

                        {(!billTo || billTo === LISCase.billing_type.GLOBAL) &&
                          insurance && (
                            <>
                              <dt>Primary Payor:</dt>
                              <dd>{insurance.insurance_company_name}</dd>

                              <dt>Subscriber Name:</dt>
                              <dd>
                                {insurance.insured_last_name},{" "}
                                {insurance.insured_first_name}
                              </dd>

                              <dt>Subscriber DOB:</dt>
                              <dd>
                                {insurance.insured_date_of_birth && // 1900-01-01
                                  DateTime.fromFormat(
                                    insurance.insured_date_of_birth,
                                    "yyyy-MM-dd"
                                  ).toFormat("MM/dd/yyyy")}
                              </dd>

                              <dt>Subscriber ID:</dt>
                              <dd>{insurance.policy_number}</dd>

                              <dt>Group Number:</dt>
                              <dd>{insurance.group_employer_id}</dd>

                              <dt>Relation to Subscriber:</dt>
                              <dd>{insurance.relationship_to_patient}</dd>

                              <dt>Address for Claims:</dt>
                              <dd>
                                {[
                                  insurance.insurance_company_address,
                                  insurance.insurance_company_address2,
                                  insurance.insurance_company_city,
                                  insurance.insurance_company_state,
                                ]
                                  .filter((v) => v)
                                  .join(", ")}
                                {insurance.insurance_company_zipcode && (
                                  <>
                                    {" "}
                                    {dashZipcode(
                                      insurance.insurance_company_zipcode
                                    )}
                                  </>
                                )}
                              </dd>
                            </>
                          )}
                      </dl>
                    </td>
                  </tr>
                </tbody>
              </table>
            </section>
            {!!specimens?.length && (
              <section className="SendoutRequisitionClinicalInformation">
                <table>
                  <thead>
                    <tr>
                      <th colSpan={2}>Clinical Information</th>
                      <th>ICD-10</th>
                    </tr>
                  </thead>
                  <tbody>
                    {specimens?.map((s) => (
                      <tr key={s.id}>
                        <td>{s.name}</td>
                        <td>{s.clinical_history}</td>
                        <td>
                          {s.icd10codes?.length ? (
                            s.icd10codes.map((c) => c.code).join(", ")
                          ) : (
                            <select>
                              <option value=""></option>
                              <option value="D48.5">D48.5</option>
                              <option value="R23.8">R23.8</option>
                            </select>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}
            <section
              className={classNames("SendoutRequisitionNotes", {
                "SendoutRequisitionNotes--hidden": !notes,
              })}
            >
              <table>
                <thead>
                  <tr>
                    <th colSpan={2}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="SendoutRequisition__Fillable">
                      <textarea
                        rows={4}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                      ></textarea>
                    </td>
                  </tr>
                </tbody>
              </table>
            </section>
            <section className="SendoutRequisitionOrders">
              <table>
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={!hiddenOrderIds.length}
                        onChange={() => {
                          console.log(hiddenOrderIds);
                          !hiddenOrderIds.length
                            ? setHiddenOrderIds(
                                orders.map((w) => w.id) as number[]
                              )
                            : setHiddenOrderIds([]);
                        }}
                      />
                    </th>
                    <th>Accession </th>
                    <th>BLK</th>
                    <th>Order</th>
                    <th>Tissue Type</th>
                    <th>Body Site</th>
                    <th>Collected</th>
                    <th>CTRL Label</th>
                    <th>Slide Label</th>
                  </tr>
                </thead>

                <tbody>
                  {orders.map((order) => (
                    <OrderRow
                      key={order.id}
                      lisCase={lisCase}
                      order={order}
                      hiddenOrderIds={hiddenOrderIds}
                      setHiddenOrderIds={setHiddenOrderIds}
                    />
                  ))}
                </tbody>
              </table>
            </section>
            {sendoutOrders.at(-1) && (
              <section className="SendoutRequisitionElectronicSignature">
                <p>
                  <strong>Electronically Signed By:</strong>{" "}
                  {sendingUser &&
                    sendingUser.email !== "tbd@pathologywatch.com" && (
                      <>
                        <UserFullName
                          id={lisCase.active_assignment_id}
                          showCredentials
                        />{" "}
                        {DateTime.fromISO(
                          sendoutOrders.at(-1)?.created_at || ""
                        ).toLocaleString({
                          ...DateTime.DATETIME_SHORT_WITH_SECONDS,
                          timeZone:
                            sendingLab?.location?.timezone ||
                            Intl.DateTimeFormat().resolvedOptions().timeZone,
                          timeZoneName: "longGeneric",
                        })}
                      </>
                    )}
                </p>
                {sendingUser &&
                  sendingUser.email !== "tbd@pathologywatch.com" && (
                    <p>
                      <strong>Ordering Pathologist:</strong>{" "}
                      <UserFullName
                        id={lisCase.active_assignment_id}
                        showCredentials
                      />
                    </p>
                  )}
                <p>
                  <strong>Ordering Lab:</strong> {sendingLab?.name}
                </p>
                <p>
                  <strong>Lab Address:</strong>{" "}
                  <span>
                    {[
                      sendingLab?.location?.street,
                      sendingLab?.location?.street2,
                      sendingLab?.location?.city,
                      sendingLab?.location?.state,
                    ]
                      .filter((v) => v)
                      .join(", ")}
                    {sendingLab?.location?.zipcode && (
                      <> {dashZipcode(sendingLab?.location?.zipcode)}</>
                    )}
                  </span>
                </p>
              </section>
            )}
          </div>
        </tbody>
      </table>
    </article>
  );
};

export default SendoutRequisition;
